import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { DATA_ISSUES } from './constants';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import type { DataIssue } from '../profiles/types';

// Types
interface GetDataIssuesParams extends MedScout.Pagination, MedScout.Sortable, MedScout.Searchable {}

interface GetDataIssueDetailsParams {
  dataIssueId: string;
}

interface SubmitDataIssueParams {
  issue_type: string;
  provider_id: string;
  new_data?: object;
  comments?: string;
}

interface ResolveDataIssueParams {
  dataIssueId: string;
  newData: object;
  status: string;
}

interface SearchAddressesParams {
  search?: string;
}

interface GetDataIssueEmailSuggestionsParams {
  issueType: string; // e.g., 'email'
  providerId: string;
  providerType: string; // e.g., 'hcp', 'clinic', 'center'
}

interface GetDataIssueAddressSuggestionsParams {
  providerId: string;
}

export interface AddressSuggestionItem {
  id?: string;
  address_key: string;
  practice_name?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip_code: string;
  lat?: number;
  lng?: number;
  is_primary?: boolean;
  [key: string]: any;
}

export interface AddressSuggestionsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AddressSuggestionItem[];
}

export interface EmailSuggestionItem {
  email: string;
  is_primary?: boolean;
  confidence?: number;
  [key: string]: any;
}

export interface EmailSuggestionsResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: EmailSuggestionItem[];
}

// Base API for all data issues endpoints
export const dataIssuesApi = createApi({
  reducerPath: 'dataIssues',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [DATA_ISSUES],
  endpoints: (builder) => ({
    // Query: Get list of data issues with pagination, sorting, and search
    getDataIssues: builder.query<MedScout.PaginatedResponse & { results: DataIssue[] }, GetDataIssuesParams>({
      query: ({ q, sort, order, page, pageSize }) => {
        const queryParams = getSearchQueryParamsForAPI({
          sort,
          order,
          page: page + 1,
          pageSize,
          q,
        });
        return {
          url: `/v1/account/data-issues/${queryParams}`,
          method: 'GET',
        };
      },
      providesTags: () => [{ type: DATA_ISSUES, id: 'LIST' }],
    }),

    // Query: Get details of a specific data issue
    getDataIssueDetails: builder.query<DataIssue, GetDataIssueDetailsParams>({
      query: ({ dataIssueId }) => ({
        url: `/v1/account/data-issues/${dataIssueId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { dataIssueId }) => [
        { type: DATA_ISSUES, id: dataIssueId },
      ],
    }),

    // Query: Search addresses
    searchAddresses: builder.query<{ results: AddressSuggestionItem[] }, SearchAddressesParams>({
      query: ({ search = '' }) => {
        const queryParams = getSearchQueryParamsForAPI({
          q: search,
        });
        return {
          url: `/v1/entities/address/${queryParams}`,
          method: 'GET',
        };
      },
    }),

    // Query: Get email suggestions for a data issue
    getDataIssueEmailSuggestions: builder.query<
      EmailSuggestionsResponse,
      GetDataIssueEmailSuggestionsParams
    >({
      query: ({ issueType, providerId, providerType }) => ({
        url: `/v1/${providerType}/${providerId}/${issueType}/`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        if (response?.length > 0) {
          return {
            results: [...response].sort(
              (a, b) => Number(b.is_primary ?? 0) - Number(a.is_primary ?? 0)
            ),
            count: response.length,
            next: null,
            previous: null,
          } as EmailSuggestionsResponse;
        }

        // Return default empty structure if response is not as expected
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
        } as EmailSuggestionsResponse;
      },
    }),

    // Query: Get address suggestions for a data issue
    getDataIssueAddressSuggestions: builder.query<
      AddressSuggestionsResponse,
      GetDataIssueAddressSuggestionsParams
    >({
      query: ({ providerId }) => ({
        url: `/v1/entities/entity/${providerId}/address/`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        if (response?.results && Array.isArray(response.results)) {
          return {
            ...response,
            results: [...response.results].sort(
              (a, b) => Number(b.is_primary ?? 0) - Number(a.is_primary ?? 0)
            ),
          } as AddressSuggestionsResponse;
        }

        // Return default structure if response is not as expected
        return {
          count: 0,
          next: null,
          previous: null,
          results: [],
        } as AddressSuggestionsResponse;
      },
    }),

    // Mutation: Submit a new data issue
    submitDataIssue: builder.mutation<DataIssue, SubmitDataIssueParams>({
      query: (data) => ({
        url: `/v1/account/data-issues/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: DATA_ISSUES, id: 'LIST' }],
    }),

    // Mutation: Resolve a data issue
    resolveDataIssue: builder.mutation<DataIssue, ResolveDataIssueParams>({
      query: ({ dataIssueId, newData, status }) => ({
        url: `/v1/account/data-issues/${dataIssueId}/`,
        method: 'PATCH',
        body: {
          data_issue_id: dataIssueId,
          new_data: newData,
          status: status,
        },
      }),
      invalidatesTags: (result, error, { dataIssueId }) => [
        { type: DATA_ISSUES, id: 'LIST' },
        { type: DATA_ISSUES, id: dataIssueId },
      ],
    }),
  }),
});

export const {
  useGetDataIssuesQuery,
  useGetDataIssueDetailsQuery,
  useSearchAddressesQuery,
  useGetDataIssueEmailSuggestionsQuery,
  useGetDataIssueAddressSuggestionsQuery,
  useSubmitDataIssueMutation,
  useResolveDataIssueMutation,
} = dataIssuesApi;
